/**
 * FORGOT YOUR PASSWORD? (`/forgot`, then `/reset#<token>`, migration 0041).
 *
 * The person gives their email; if an account uses it, Leveri emails a link. The page says the same thing either
 * way, because the server does: it never tells anyone whether an account exists. The link carries its token in
 * the URL FRAGMENT, which a browser never sends to a server; it leaves the page only inside a request body. Every
 * way a link can fail reads the same.
 *
 * Mounted BEFORE the app's sign-in gate (main.tsx), with no session call. Setting the new password signs the
 * person in and opens Leveri; every older session is signed out.
 */
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import type { z } from "zod";
import { ErrorEnvelope, PASSWORD_MIN_LENGTH, PASSWORD_RULES, PasswordResetCheckResponse, PasswordResetRequestResponse, SessionResponse } from "@leveri/contracts";
import { Button, Notice, Reading, TextField } from "../file/index.js";
import { Wordmark } from "./shell.js";

async function post<T extends z.ZodTypeAny>(path: string, schema: T, body: unknown): Promise<z.infer<T>> {
  const res = await fetch(path, {
    method: "POST", credentials: "same-origin", body: JSON.stringify(body),
    headers: { accept: "application/json", "content-type": "application/json" },
  });
  const payload: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const envelope = ErrorEnvelope.safeParse(payload);
    throw new Error(envelope.success ? envelope.data.error.message : "Something went wrong. Try again in a moment.");
  }
  return schema.parse(payload);
}

const TALL = "!h-11 !text-[15px]";

function Frame({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-6 py-14 text-ink">
      <div className="w-full max-w-[380px]">
        <div className="mb-10"><Wordmark /></div>
        {children}
      </div>
    </main>
  );
}

export function ForgotPasswordPage() {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function ask(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const email = String(new FormData(event.currentTarget).get("email") ?? "").trim();
    setBusy(true);
    setFailure(null);
    try {
      await post("/api/public/password-reset", PasswordResetRequestResponse, { email });
      setSentTo(email);
    } catch (err) {
      setFailure(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Frame>
      {sentTo ? (
        <div data-testid="forgot-sent">
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Check your email</h1>
          <p className="mt-2 text-[15px] leading-relaxed text-ink-soft">
            If a Leveri account uses <span className="text-ink">{sentTo}</span>, we&rsquo;ve sent it a link to set a new password. The link works once, for one hour.
          </p>
          <a href="/" className="mt-6 inline-block text-sm text-ink underline underline-offset-2">Back to sign in</a>
        </div>
      ) : (
        <>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Forgot your password?</h1>
          <p className="mt-1.5 text-sm text-stone">Give the email you sign in with, and we&rsquo;ll send a link to set a new one.</p>
          <form onSubmit={ask} className="mt-8 flex flex-col gap-5">
            <TextField name="email" type="email" label="Work email" required autoComplete="username" className={TALL} />
            {failure && <Notice>{failure}</Notice>}
            <Button type="submit" disabled={busy} className="mt-1 !h-11 w-full !text-[15px]" data-testid="forgot-submit">Send the link</Button>
            {busy && <Reading>Sending…</Reading>}
          </form>
          <a href="/" className="mt-6 inline-block text-sm text-stone underline underline-offset-2 hover:text-ink">Back to sign in</a>
        </>
      )}
    </Frame>
  );
}

export function ResetPasswordPage() {
  const [token] = useState(() => window.location.hash.replace(/^#/, ""));
  const [email, setEmail] = useState<string | null>(null);
  const [unusable, setUnusable] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    post("/api/public/password-reset/check", PasswordResetCheckResponse, { token })
      .then((r) => setEmail(r.email))
      .catch((err: unknown) => setUnusable(err instanceof Error ? err.message : String(err)));
  }, [token]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const password = String(new FormData(event.currentTarget).get("password") ?? "");
    setBusy(true);
    setFailure(null);
    try {
      await post("/api/public/password-reset/use", SessionResponse, { token, password });
      // Signed in: the token leaves the address bar with the page.
      window.location.replace("/");
    } catch (err) {
      setFailure(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <Frame>
      {!email && !unusable && <Reading>Opening the link…</Reading>}
      {unusable && (
        <>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">This link cannot be used</h1>
          <p className="mt-2 text-sm text-stone" data-testid="reset-invalid">{unusable}</p>
          <a href="/forgot" className="mt-6 inline-block text-sm text-ink underline underline-offset-2">Ask for a new link</a>
        </>
      )}
      {email && (
        <>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Set a new password</h1>
          <p className="mt-1.5 text-sm text-stone">For <span className="text-ink" data-testid="reset-email">{email}</span>. Every other place you&rsquo;re signed in will be signed out.</p>
          <form onSubmit={save} className="mt-8 flex flex-col gap-5">
            <TextField name="password" type="password" label="New password" hint={PASSWORD_RULES.join(" ")} required minLength={PASSWORD_MIN_LENGTH} autoComplete="new-password" className={TALL} />
            {failure && <Notice>{failure}</Notice>}
            <Button type="submit" disabled={busy} className="mt-1 !h-11 w-full !text-[15px]" data-testid="reset-submit">Set the password and sign in</Button>
            {busy && <Reading>Setting it…</Reading>}
          </form>
        </>
      )}
    </Frame>
  );
}
